import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, fetchWithRetry } from '../config/api';

export interface PriceData {
  symbol: string;
  price: number;
  timestamp: number;
  source: string;
  confidence: number;
  priceUSD?: number;
}

export interface HealthData {
  status: string;
  uptime: number;
  version: string;
  services: {
    database: boolean;
    redis: boolean;
    alephiumNode: boolean;
    externalApis: boolean;
  };
  responseTime: number;
}

export interface MetricsData {
  system: {
    status: string;
    uptime: number;
    responseTime: number;
    memory: any;
    version: string;
  };
  database: {
    connected: boolean;
    totalPrices: number;
    totalTokens: number;
    lastUpdate: number | null;
  };
  oracle: {
    activePrices: number;
    avgConfidence: number;
    totalLiquidity: number;
    topTokens: Array<{
      symbol: string;
      confidence: number;
      priceInAlph: number;
    }>;
  };
  endpoints: Record<string, string>;
}

export const useOracleAPI = () => {
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [allPrices, setAllPrices] = useState<PriceData[]>([]);
  const [symbols, setSymbols] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch health data
  const fetchHealth = useCallback(async () => {
    try {
      const response = await fetchWithRetry(API_ENDPOINTS.health);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setHealthData(data);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
      setError(`Failed to fetch health data: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Set a default health state
      setHealthData({
        status: 'error',
        uptime: 0,
        version: 'unknown',
        services: {
          database: false,
          redis: false,
          alephiumNode: false,
          externalApis: false
        },
        responseTime: 0
      });
    }
  }, []);

  // Fetch metrics data
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetchWithRetry(API_ENDPOINTS.metrics);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setMetricsData(data);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      // Set default metrics on error
      setMetricsData({
        system: {
          status: 'error',
          uptime: 0,
          responseTime: 0,
          memory: {},
          version: 'unknown'
        },
        database: {
          connected: false,
          totalPrices: 0,
          totalTokens: 0,
          lastUpdate: null
        },
        oracle: {
          activePrices: 0,
          avgConfidence: 0,
          totalLiquidity: 0,
          topTokens: []
        },
        endpoints: {}
      });
    }
  }, []);

  // Fetch all prices
  const fetchPrices = useCallback(async () => {
    try {
      const response = await fetchWithRetry(API_ENDPOINTS.prices);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      
      // Transform the prices object to array format
      const pricesArray = Object.entries(data.prices || {}).map(([symbol, priceData]: [string, any]) => ({
        symbol,
        price: priceData.priceInAlph || 0,
        timestamp: priceData.lastUpdated || Date.now(),
        source: priceData.dex || 'cache',
        confidence: priceData.confidence || 0,
        priceUSD: priceData.priceUSD
      }));
      
      setAllPrices(pricesArray);
      if (pricesArray.length === 0) {
        console.log('No price data available from API');
      }
    } catch (err) {
      console.error('Failed to fetch prices:', err);
      setError(`Failed to fetch prices: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setAllPrices([]); // Set empty array on error
    }
  }, []);

  // Fetch available symbols
  const fetchSymbols = useCallback(async () => {
    try {
      const response = await fetchWithRetry(API_ENDPOINTS.symbols);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setSymbols(data.symbols || []);
      if ((data.symbols || []).length === 0) {
        console.log('No symbols available from API');
      }
    } catch (err) {
      console.error('Failed to fetch symbols:', err);
      setError(`Failed to fetch symbols: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSymbols([]); // Set empty array on error
    }
  }, []);

  // Fetch specific token price
  const fetchTokenPrice = useCallback(async (symbol: string): Promise<PriceData | null> => {
    try {
      const response = await fetchWithRetry(API_ENDPOINTS.price(symbol));
      const data = await response.json();
      
      return {
        symbol: data.symbol,
        price: data.priceInAlph || 0,
        timestamp: data.lastUpdated || Date.now(),
        source: data.dex || 'unknown',
        confidence: data.confidence || 0
      };
    } catch (err) {
      console.error(`Failed to fetch price for ${symbol}:`, err);
      return null;
    }
  }, []);

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          fetchHealth(),
          fetchMetrics(),
          fetchPrices(),
          fetchSymbols()
        ]);
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setError('Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [fetchHealth, fetchMetrics, fetchPrices, fetchSymbols]);

  // Refresh data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      fetchHealth();
      fetchMetrics();
      fetchPrices();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [fetchHealth, fetchMetrics, fetchPrices]);

  return {
    healthData,
    metricsData,
    allPrices,
    symbols,
    loading,
    error,
    refetch: {
      health: fetchHealth,
      metrics: fetchMetrics,
      prices: fetchPrices,
      symbols: fetchSymbols,
      tokenPrice: fetchTokenPrice
    }
  };
};
